import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FilePen, Plus, X, AlertTriangle, CheckCircle, Clock, Send, FileSignature, Trash2, ExternalLink, RotateCcw, ChevronDown } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

const CONTRACT_TYPES = [
  { value: 'embauche',           label: 'Contrat d\'embauche' },
  { value: 'renouvellement_cdd', label: 'Renouvellement CDD' },
  { value: 'avenant_poste',      label: 'Avenant — Changement de poste' },
  { value: 'avenant_horaires',   label: 'Avenant — Changement horaires' },
  { value: 'transfert',          label: 'Transfert inter-établissement' },
  { value: 'autre',              label: 'Autre document RH' },
]

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  draft:   { label: 'Brouillon',        color: 'bg-gray-100 text-gray-700',    icon: FilePen },
  sent:    { label: 'En attente sign.', color: 'bg-blue-100 text-blue-700',    icon: Clock },
  signed:  { label: 'Signé',            color: 'bg-green-100 text-green-700',  icon: CheckCircle },
  refused: { label: 'Refusé',           color: 'bg-red-100 text-red-700',      icon: X },
  expired: { label: 'Expiré',           color: 'bg-amber-100 text-amber-700',  icon: AlertTriangle },
}

type Tab = 'contracts' | 'alerts'

const EMPTY_FORM = {
  employeeId: '', type: 'embauche', title: '',
  contractStartDate: '', contractEndDate: '', notes: ''
}

export default function Contracts() {
  const { current: currentEstab } = useEstablishment()
  const [tab, setTab] = useState<Tab>('contracts')
  const [contracts, setContracts] = useState<any[]>([])
  const [alerts, setAlerts] = useState<any>({ expiringContracts: [], pendingSignatures: [], missingDocs: [] })
  const [employees, setEmployees] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<{ open: boolean; contract?: any } | null>(null)
  const [signModal, setSignModal] = useState<any>(null)
  const [signEmail, setSignEmail] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterType, setFilterType] = useState('')

  useEffect(() => { load() }, [currentEstab])

  async function load() {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (currentEstab) params.set('establishmentId', currentEstab.id)
      const [contractsR, alertsR, empR] = await Promise.all([
        api.get(`/contracts?${params}`),
        api.get(`/contracts/alerts?${params}`),
        api.get(`/employees?status=active${currentEstab ? `&establishmentId=${currentEstab.id}` : ''}`)
      ])
      setContracts(contractsR.data)
      setAlerts(alertsR.data)
      setEmployees(empR.data)
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setForm({ ...EMPTY_FORM, employeeId: employees[0]?.id || '' })
    setModal({ open: true })
  }

  function openEdit(c: any) {
    setForm({
      employeeId: c.employeeId,
      type: c.type,
      title: c.title,
      contractStartDate: c.contractStartDate || '',
      contractEndDate: c.contractEndDate || '',
      notes: c.notes || ''
    })
    setModal({ open: true, contract: c })
  }

  async function save() {
    setSaving(true)
    try {
      if (modal?.contract) {
        await api.put(`/contracts/${modal.contract.id}`, form)
      } else {
        await api.post('/contracts', { ...form, establishmentId: currentEstab?.id })
      }
      await load()
      setModal(null)
    } finally {
      setSaving(false)
    }
  }

  async function sendSignature() {
    if (!signEmail || !signModal) return
    setSaving(true)
    try {
      await api.post(`/contracts/${signModal.id}/send-signature`, { email: signEmail })
      await load()
      setSignModal(null)
      setSignEmail('')
    } finally {
      setSaving(false)
    }
  }

  async function markSigned(id: string) {
    await api.post(`/contracts/${id}/mark-signed`)
    await load()
  }

  async function markRefused(id: string) {
    if (!confirm('Marquer ce contrat comme refusé ?')) return
    await api.post(`/contracts/${id}/refuse`)
    await load()
  }

  async function deleteContract(id: string) {
    if (!confirm('Supprimer ce contrat ?')) return
    await api.delete(`/contracts/${id}`)
    await load()
  }

  function autoTitle() {
    const emp = employees.find(e => e.id === form.employeeId)
    const type = CONTRACT_TYPES.find(t => t.value === form.type)
    if (emp && type) {
      setForm(f => ({ ...f, title: `${type.label} — ${emp.firstName} ${emp.lastName}` }))
    }
  }

  const alertCount = alerts.expiringContracts.length + alerts.pendingSignatures.length + alerts.missingDocs.length

  const filtered = contracts.filter(c =>
    (!filterStatus || c.status === filterStatus) &&
    (!filterType   || c.type   === filterType)
  )

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Contrats & RH</h1>
          <p className="text-sm text-gray-500 mt-0.5">Contrats, avenants, signatures et dossiers administratifs</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          <Plus size={15} /> Nouveau document
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button onClick={() => setTab('contracts')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === 'contracts' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Tous les documents ({contracts.length})
        </button>
        <button onClick={() => setTab('alerts')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === 'alerts' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          Alertes
          {alertCount > 0 && <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{alertCount}</span>}
        </button>
      </div>

      {/* ── Tab Contrats ── */}
      {tab === 'contracts' && (
        <>
          {/* Filtres */}
          <div className="flex items-center gap-3 mb-4">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_CONFIG).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
              <option value="">Tous les types</option>
              {CONTRACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {(filterStatus || filterType) && (
              <button onClick={() => { setFilterStatus(''); setFilterType('') }} className="text-xs text-gray-500 underline">Effacer</button>
            )}
          </div>

          {/* Liste */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <p className="text-center text-gray-400 py-12 text-sm">Chargement...</p>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center">
                <FilePen size={32} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-400 text-sm">Aucun contrat enregistré</p>
                <button onClick={openCreate} className="text-blue-600 text-sm mt-2 hover:text-blue-800">Créer le premier document</button>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Document</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Employé</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Dates</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => {
                    const status = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft
                    const StatusIcon = status.icon
                    const typeLabel = CONTRACT_TYPES.find(t => t.value === c.type)?.label || c.type
                    return (
                      <tr key={c.id} className={`border-t border-gray-100 hover:bg-gray-50 ${i === 0 ? 'border-t-0' : ''}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{c.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{typeLabel}</p>
                        </td>
                        <td className="px-4 py-3">
                          {c.employee ? (
                            <Link to={`/employees/${c.employee.id}`} className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                              {c.employee.firstName} {c.employee.lastName}
                              <ExternalLink size={11} />
                            </Link>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {c.contractStartDate && <div>Début : {c.contractStartDate}</div>}
                          {c.contractEndDate   && <div>Fin : {c.contractEndDate}</div>}
                          {c.signatureRequestedAt && <div className="text-blue-600 mt-0.5">Envoyé : {new Date(c.signatureRequestedAt).toLocaleDateString('fr-FR')}</div>}
                          {c.signatureSignedAt    && <div className="text-green-600 mt-0.5">Signé : {new Date(c.signatureSignedAt).toLocaleDateString('fr-FR')}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${status.color}`}>
                            <StatusIcon size={11} />{status.label}
                          </span>
                          {c.signatureEmail && c.status === 'sent' && (
                            <p className="text-xs text-gray-400 mt-1">→ {c.signatureEmail}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {c.status === 'draft' && (
                              <button onClick={() => { setSignModal(c); setSignEmail(c.employee?.email || '') }}
                                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-50 font-medium">
                                <Send size={12} /> Envoyer à signer
                              </button>
                            )}
                            {c.status === 'sent' && (
                              <>
                                <button onClick={() => markSigned(c.id)}
                                  className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50 font-medium">
                                  <CheckCircle size={12} /> Marquer signé
                                </button>
                                <button onClick={() => markRefused(c.id)}
                                  className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50">
                                  <X size={12} /> Refusé
                                </button>
                              </>
                            )}
                            <button onClick={() => openEdit(c)}
                              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100">
                              Modifier
                            </button>
                            <button onClick={() => deleteContract(c.id)}
                              className="text-gray-300 hover:text-red-500 p-1 rounded">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── Tab Alertes ── */}
      {tab === 'alerts' && (
        <div className="space-y-6">
          {/* Fins de contrat */}
          <div>
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
              Fins de contrat imminentes ({alerts.expiringContracts.length})
            </h2>
            {alerts.expiringContracts.length === 0 ? (
              <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-sm text-green-700 flex items-center gap-2">
                <CheckCircle size={16} /> Aucun contrat arrivant à échéance dans les 60 jours
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Employé</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Fin le</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Reste</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.expiringContracts.map((e: any) => (
                      <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link to={`/employees/${e.id}`} className="font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1">
                            {e.firstName} {e.lastName} <ExternalLink size={11} />
                          </Link>
                          <p className="text-xs text-gray-400">{e.establishmentName}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{e.contractType}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{e.contractEndDate}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${e.urgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {e.daysLeft}j
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => {
                            setForm({ ...EMPTY_FORM, employeeId: e.id, type: 'renouvellement_cdd', title: `Renouvellement CDD — ${e.firstName} ${e.lastName}` })
                            setModal({ open: true })
                          }} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                            Créer renouvellement
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Signatures en attente */}
          {alerts.pendingSignatures.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
                Signatures en attente &gt; 5 jours ({alerts.pendingSignatures.length})
              </h2>
              <div className="space-y-2">
                {alerts.pendingSignatures.map((c: any) => (
                  <div key={c.id} className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{c.title}</p>
                      <p className="text-xs text-gray-500">{c.employee?.firstName} {c.employee?.lastName} · Envoyé à {c.signatureEmail}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => markSigned(c.id)}
                        className="text-xs text-green-600 hover:text-green-800 font-medium px-2 py-1 rounded hover:bg-green-50">
                        Marquer signé
                      </button>
                      <button onClick={() => { setSignModal(c); setSignEmail(c.signatureEmail || '') }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-100">
                        Renvoyer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents manquants */}
          <div>
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
              Dossiers incomplets ({alerts.missingDocs.length})
            </h2>
            {alerts.missingDocs.length === 0 ? (
              <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-sm text-green-700 flex items-center gap-2">
                <CheckCircle size={16} /> Tous les dossiers sont complets
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Employé</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Documents manquants</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.missingDocs.map((e: any) => (
                      <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link to={`/employees/${e.id}`} className="font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1">
                            {e.firstName} {e.lastName} <ExternalLink size={11} />
                          </Link>
                          <p className="text-xs text-gray-400">{e.establishmentName}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {e.missing.map((m: string) => (
                              <span key={m} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{m}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/employees/${e.id}`} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                            Compléter →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal création/édition ── */}
      {modal?.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <FileSignature size={18} className="text-indigo-600" />
                <h2 className="font-bold text-gray-900">{modal.contract ? 'Modifier le document' : 'Nouveau document RH'}</h2>
              </div>
              <button onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {!modal.contract && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employé *</label>
                  <select value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Sélectionner un employé</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de document *</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {CONTRACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <div className="flex gap-2">
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="ex: Contrat CDI — Thomas Martin"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button type="button" onClick={autoTitle}
                    className="text-xs text-blue-600 border border-blue-300 hover:bg-blue-50 px-3 rounded-lg">Auto</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                  <input type="date" value={form.contractStartDate}
                    onChange={e => setForm(f => ({ ...f, contractStartDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                  <input type="date" value={form.contractEndDate}
                    onChange={e => setForm(f => ({ ...f, contractEndDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} rows={3} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Informations complémentaires..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={save} disabled={saving || !form.title || !form.type || (!modal.contract && !form.employeeId)}
                className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal envoi signature ── */}
      {signModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-2">
                <Send size={18} className="text-indigo-600" />
                <h2 className="font-bold text-gray-900">Envoyer à signature</h2>
              </div>
              <button onClick={() => setSignModal(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                <p className="text-sm font-medium text-indigo-800">{signModal.title}</p>
                {signModal.employee && (
                  <p className="text-xs text-indigo-600 mt-0.5">{signModal.employee.firstName} {signModal.employee.lastName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email du signataire *</label>
                <input type="email" value={signEmail} onChange={e => setSignEmail(e.target.value)}
                  placeholder="employe@email.fr"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                <p className="text-xs text-gray-400 mt-1">Un email avec un lien de signature sera envoyé à cette adresse.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setSignModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={sendSignature} disabled={saving || !signEmail}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg">
                <Send size={14} />{saving ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
